// WHAT: Table component for displaying tracked sessions with sorting
// WHY:  Provides detailed view of tracked sessions for selected period
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: useFilter hook, formatMinutes utility, core.invoke
// RISKS:
//   - NOTE: Sorting may not work correctly with null/undefined values
//   - NOTE: Table may have performance issues with many sessions
//   - NOTE: Date/time formatting may not match user locale

import type { JSX } from "react";

import { core } from "@tauri-apps/api";
import { useState, useEffect, useMemo } from "react";

import { Card } from "@/components/ui";

import { formatMinutes } from "../../utils/format";
import { useFilter } from "../molecules/FilterContextProvider.shared";

/**
 * Tracked session interface
 */
interface TrackedSession {
  /** Aspect name */
  aspect: string;
  /** Duration in seconds */
  duration_seconds: number;
  /** End time (ISO) */
  end_time: string;
  /** Optional notes */
  notes?: null | string;
  /** Start time (ISO) */
  start_time: string;
  /** Workspace name */
  workspace: string;
}

/**
 * Backend response interface
 */
interface BackendResponse {
  /** Period sessions */
  period_sessions?: TrackedSession[];
}

/**
 * Sort key type
 */
type SortKey = "aspect" | "duration" | "time" | "workspace";

/**
 * WHAT:
 *   Gets sort value for a session based on sort key.
 *
 * @param s - Tracked session
 *
 * @param key - Sort key
 *
 * @returns Sort value (number or string)
 *
 */
const getSortValue = (s: TrackedSession, key: SortKey): number | string => {
  switch (key) {
    case "time": {
      return s.start_time;
    }
    case "workspace": {
      return s.workspace.toLowerCase();
    }
    case "aspect": {
      return s.aspect.toLowerCase();
    }
    case "duration": {
      return s.duration_seconds;
    }
  }
};

/**
 * WHAT:
 *   Table component that displays tracked sessions for the selected filter
 *   period with sorting capabilities.
 *
 * WHY:
 *   Enables users to review their tracked sessions in a structured table
 *   format with sorting options.
 *
 * BEHAVIOR:
 *   - Fetches tracked sessions from backend
 *   - Displays sessions in sortable table
 *   - Supports sorting by time, workspace, aspect, duration
 *   - Updates when filter changes
 *
 * RISKS:
 *   - NOTE: Sorting may not work correctly with null/undefined values
 *   - NOTE: Table may have performance issues with many sessions
 *   - NOTE: Date/time formatting may not match user locale
 *
 * @returns JSX.Element
 *
 */
export const TrackedTodayTable = () => {
  const { dateFilter, period } = useFilter();

  const [rows, setRows] = useState<TrackedSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);

  const [sortKey, setSortKey] = useState<SortKey>("time");
  const [asc, setAsc] = useState(true);

  /* ----- Backend laden ----- */
  useEffect(() => {
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await core.invoke<BackendResponse>("get_aggregated_summary", {
          filter: dateFilter,
        });
        setRows(res.period_sessions ?? []);
      } catch {
        // session table load error
        setError("Fehler beim Laden");
      } finally {
        setLoading(false);
      }
    })();
  }, [dateFilter]);

  /* ----- Sortierung ----- */
  const sorted = useMemo(() => {
    const r = [...rows];
    r.sort((a, b) => {
      const v1 = getSortValue(a, sortKey);
      const v2 = getSortValue(b, sortKey);
      if (v1 < v2) {
        return asc ? -1 : 1;
      }
      if (v1 > v2) {
        return asc ? 1 : -1;
      }
      return 0;
    });
    return r;
  }, [rows, sortKey, asc]);

  const toggleSort = (k: SortKey) => {
    setAsc(k === sortKey ? !asc : true);
    setSortKey(k);
  };

  /**
   * WHAT:
   *   Returns table content based on loading, error, or data state.
   *
   * @returns JSX.Element for table content
   *
   */
  const getTableContent = (): JSX.Element => {
    if (loading) {
      return <Skeleton text="Loading…" />;
    }

    if (error) {
      return <ErrorBox text={error} />;
    }

    if (rows.length === 0) {
      return <Skeleton text="Keine Sessions im Zeitraum" />;
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-slate-300">
          <thead>
            <tr className="border-b border-slate-600">
              <Th
                active={sortKey === "time"}
                asc={asc}
                label="Time"
                onClick={() => {
                  toggleSort("time");
                }}
              />
              <Th
                active={sortKey === "workspace"}
                asc={asc}
                label="Workspace"
                onClick={() => {
                  toggleSort("workspace");
                }}
              />
              <Th
                active={sortKey === "aspect"}
                asc={asc}
                label="Aspect"
                onClick={() => {
                  toggleSort("aspect");
                }}
              />
              <th className="px-4 py-2 text-left">Notes</th>
              <Th
                active={sortKey === "duration"}
                asc={asc}
                label="Duration"
                onClick={() => {
                  toggleSort("duration");
                }}
              />
            </tr>
          </thead>
          <tbody>
            {sorted.map((s) => (
              <tr
                className="border-b border-slate-700 hover:bg-slate-700/50 transition-colors"
                key={s.start_time + s.aspect}
              >
                <td className="px-4 py-1">
                  {new Date(s.start_time).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </td>
                <td className="px-4 py-1">{s.workspace}</td>
                <td className="px-4 py-1">{s.aspect}</td>
                <td className="px-4 py-1">{s.notes ?? "–"}</td>
                <td className="px-4 py-1 text-right">
                  {formatMinutes(Math.round(s.duration_seconds / 60))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  /* ----- Render ----- */
  return (
    <Card
      className="bg-slate-800/30 border-slate-600/50 shadow-tracq-card"
      padding="lg"
      variant="elevated"
    >
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Sessions&nbsp;({period})</h3>

        {getTableContent()}
      </div>
    </Card>
  );
};

/* ----- Table-Header mit Sort-Icon ----- */
/**
 * WHAT:
 *   Table header component with sort indicator.
 *
 * @param root0
 *
 * @param root0.label
 *
 * @param root0.onClick
 *
 * @param root0.active
 *
 * @param root0.asc
 *
 * @returns JSX.Element
 *
 */
function Th({
  active,
  asc,
  label,
  onClick,
}: Readonly<{
  label: string;
  onClick: () => void;
  active: boolean;
  asc: boolean;
}>) {
  return (
    <th
      className={`px-4 py-2 cursor-pointer select-none hover:text-white transition-colors ${
        active ? "text-white" : "text-slate-400"
      }`}
      onClick={onClick}
    >
      {label}
      {active ? <span className="ml-1 inline-block">{asc ? "▲" : "▼"}</span> : null}
    </th>
  );
}

/* ----- Hilfs-UI für Loader / „keine Daten" ----- */
/**
 * WHAT:
 *   Skeleton loader component.
 *
 * @param root0
 *
 * @param root0.text
 *
 * @returns JSX.Element
 *
 */
function Skeleton({ text }: Readonly<{ text: string }>) {
  return (
    <div className="h-40 flex items-center justify-center text-slate-500 animate-pulse">{text}</div>
  );
}
/**
 * WHAT:
 *   Error display component.
 *
 * @param root0
 *
 * @param root0.text
 *
 * @returns JSX.Element
 *
 */
function ErrorBox({ text }: Readonly<{ text: string }>) {
  return <div className="h-40 flex items-center justify-center text-error-400">{text}</div>;
}
